import * as Yup from 'yup';
import { FC, Dispatch, SetStateAction, useMemo } from 'react'
import { Notification } from "../../../types/global/types"
import "./styles.scss"
import { Button } from "../../ContactsPage/Button"
import { useFormik } from 'formik';
import { LeftTown } from '../../VisitReportPage/VisitReportModal/childrens/LeftTown';
import { useDispatch, useSelector } from 'react-redux';
import { addressSchema } from '../../../validationSchemas/addressSchema';
import { newAddressNotification } from '../../../backend/store/actions/creators/newAddressNotification';
import { confirmNewAddressNotification } from '../../../backend/store/actions/creators/confirmNewAddressNotification';
import { generalActions } from '../../../backend/store/reducers/generalReducer';
import { actions as notificationActions, notificationsTriggerActions } from '../../../backend/store/reducers/notificationsReducer';
import { useTranslation } from '../../../hooks/useTranslation';

type Props = {
  notification: Notification,
  setSelectedNotification: Dispatch<SetStateAction<Notification | null>>,
}

export const NewHouseholdAddressNotification: FC<Props> = ({
  notification,
  setSelectedNotification,
}) => {
  const dispatch = useDispatch();
  const { p } = useTranslation('notifications');
  const { p: pGeneral } = useTranslation('common');
  const storedAddressesTypes = useSelector(
      (state: any) => state.addressesStore.types
  )

  const addressesTypes = useMemo(
      () => storedAddressesTypes || [],
      [storedAddressesTypes]
  )

  // Left Town only ever stages an address (household MOVED) — house phone is staged
  // exclusively via "Update contact info" (see ContactInfoNotification), never here.
  const hasStagedAddress = !!(notification as any).newHouseholdAddress;

  // "Left town" no longer requires an address — a worker often knows a household moved but
  // not where to. With nothing staged there is no address editor to show, which left this
  // notification as a bare title above a "Confirm changes" button: nothing told the admin
  // what had happened or what they were confirming. Say it plainly instead, and surface the
  // worker's own visit notes, which are usually where the "moved to Florida" detail lives.
  const isLeftTown = !!(notification as any).isLeftTown;
  const notesText =
    notification.notes == null
      ? ''
      : typeof notification.notes === 'string'
        ? notification.notes
        : JSON.stringify(notification.notes);

  const formik = useFormik({
    initialValues: {
      // Seed the chosen contact for a staged WORK address (the draft address itself is
      // unowned; the assignment lives on the visit, surfaced as newAddressPersonId).
      new_address: {
        ...((notification.newHouseholdAddress as any) || {}),
        person_id: (notification as any).newAddressPersonId ?? null,
        set_as_household_address: (notification as any).newAddressSetPrimary ?? false,
      },
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      new_address: addressSchema
    }),
    onSubmit: values => {
      const onSuccess = () => {
        dispatch(generalActions.successAC({ message: p('new_address_confirmed'), type: 'success' }));
        dispatch(notificationActions.removeNotificationFromList(notification.id));
        dispatch(notificationsTriggerActions.getNotificationsCountTriggerAC());
        setSelectedNotification(null);
      };
      const onError = () => {
        dispatch(generalActions.errorAC({ message: p('new_address_error'), type: 'error' }));
      };
      // Apply the staged change to the household. The frontend previously only called
      // update-new-address (which updates the draft) and never confirm-new-address (which
      // applies it) — so the household kept its old details.
      const confirm = () => dispatch(confirmNewAddressNotification({
        id: notification.id,
        onSuccess,
        onError,
      }));

      if (values.new_address?.place_id) {
        // Persist any admin edits to the draft first, then apply.
        dispatch(newAddressNotification({
          id: notification.id,
          data: values.new_address,
          onSuccess: confirm,
          onError,
        }));
      } else {
        confirm();
      }
    }
  })

  return (
    <div
      className="notification"
    >
      <div className="notification__header">
        <div className="notification__title">
          <h3>
            {notification.title.startOfTitle}
            {notification.title.householdName}
            {notification.title.endOfTitle}
          </h3>
          <div className="notification__time">
            {notification.created_at_formatted}
          </div>
        </div>
      </div>
      {!hasStagedAddress && isLeftTown && (
        <div className="notification__no-address">{p('left_town_no_address')}</div>
      )}
      {!hasStagedAddress && !!notesText && (
        <div className="notification__notes">
          <div className="notification__notes-header">{pGeneral('notes')}</div>
          <div className="notification__notes-body">{notesText}</div>
        </div>
      )}
      {hasStagedAddress && (
        <LeftTown
            openLeftTown
            formik={formik as any}
            setOpenLeftTown={() => {}}
            addressesTypes={addressesTypes}
            contacts={(notification as any).householdContacts || []}
            showEditHomeDetails={false}
        />
      )}
      <div className="notification__button">
        <Button
          title={hasStagedAddress ? p('confirm_change') : p('acknowledge')}
          onClick={formik.handleSubmit}
        />
      </div>
    </div>
  )
}
